'use client';

import { useState } from 'react';
import { Loader2, Upload } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@kit/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@kit/ui/dialog';
import { Input } from '@kit/ui/input';
import { Label } from '@kit/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@kit/ui/select';
import { Textarea } from '@kit/ui/textarea';
import { Trans } from '@kit/ui/trans';

import type { DocumentTypeOption, DocumentUploadInput } from '../types';

interface DocumentUploadDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onUpload: (input: DocumentUploadInput) => Promise<void>; // i18n-ignore TS type signature, not user-facing copy
  documentTypes?: DocumentTypeOption[];
  defaultDocumentType?: string;
  acceptedFileTypes?: string;
  dialogTitle?: string;
  dialogDescription?: string;
}

/**
 * Dialog for uploading new documents with metadata
 *
 * @example
 * ```tsx
 * <DocumentUploadDialog
 *   open={uploadOpen}
 *   onOpenChange={setUploadOpen}
 *   onUpload={async (input) => {
 *     const formData = new FormData();
 *     formData.append('file', input.file);
 *     formData.append('title', input.title);
 *     await uploadDocumentAction(formData);
 *   }}
 *   documentTypes={[
 *     { value: 'photo', label: 'Photo' },
 *     { value: 'video', label: 'Video' },
 *     { value: 'pdf', label: 'PDF Document' },
 *   ]}
 * />
 * ```
 */
export function DocumentUploadDialog({
  open,
  onOpenChange,
  onUpload,
  documentTypes,
  defaultDocumentType,
  acceptedFileTypes = '.pdf,.doc,.docx,.jpg,.jpeg,.png,.gif,.mp4,.mov',
  dialogTitle,
  dialogDescription,
}: DocumentUploadDialogProps) {
  const { t } = useTranslation();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [documentType, setDocumentType] = useState(
    defaultDocumentType || documentTypes?.[0]?.value || '',
  );
  const [file, setFile] = useState<File | null>(null);
  const [isPending, setIsPending] = useState(false);

  const resetForm = () => {
    setTitle('');
    setDescription('');
    setDocumentType(defaultDocumentType || documentTypes?.[0]?.value || '');
    setFile(null);
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      resetForm();
    }
    onOpenChange(newOpen);
  };

  const handleUpload = async () => {
    if (!file || !title.trim()) return;

    setIsPending(true);
    try {
      await onUpload({
        file,
        title: title.trim(),
        documentType: documentType || undefined,
        description: description.trim() || undefined,
      });
      handleOpenChange(false);
    } finally {
      setIsPending(false);
    }
  };

  const canUpload = file !== null && title.trim().length > 0 && !isPending;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="flex max-h-[90vh] flex-col overflow-hidden sm:max-w-lg">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle>
            {dialogTitle || (
              <Trans
                i18nKey="documents:uploadDialog.title"
                defaults="Upload Document"
              />
            )}
          </DialogTitle>
          <DialogDescription>
            {dialogDescription || (
              <Trans
                i18nKey="documents:uploadDialog.description"
                defaults="Upload a document with title and optional description"
              />
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 space-y-4 overflow-y-auto py-4 pr-2">
          <div className="space-y-2">
            <Label htmlFor="upload-title">
              <Trans i18nKey="documents:fields.title" defaults="Title" />
            </Label>
            <Input
              id="upload-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              disabled={isPending}
              placeholder={t('documents:fields.titlePlaceholder')}
            />
          </div>

          {documentTypes && documentTypes.length > 0 && (
            <div className="space-y-2">
              <Label>
                <Trans
                  i18nKey="documents:fields.documentType"
                  defaults="Document Type"
                />
              </Label>
              <Select
                value={documentType}
                onValueChange={setDocumentType}
                disabled={isPending}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {documentTypes.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="upload-description">
              <Trans
                i18nKey="documents:fields.description"
                defaults="Description (optional)"
              />
            </Label>
            <Textarea
              id="upload-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              disabled={isPending}
              placeholder={t('documents:fields.descriptionPlaceholder')}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="upload-file">
              <Trans i18nKey="documents:fields.file" defaults="File" />
            </Label>
            <Input
              id="upload-file"
              type="file"
              accept={acceptedFileTypes}
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              disabled={isPending}
            />
            {file && (
              <p className="text-muted-foreground text-xs">
                {file.name} ({(file.size / 1024).toFixed(1)} KB)
              </p>
            )}
          </div>
        </div>

        <div className="flex flex-shrink-0 justify-end gap-2 border-t pt-4">
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={isPending}
          >
            <Trans i18nKey="common:cancel" defaults="Cancel" />
          </Button>
          <Button onClick={handleUpload} disabled={!canUpload}>
            {isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                <Trans i18nKey="common:uploading" defaults="Uploading..." />
              </>
            ) : (
              <>
                <Upload className="mr-2 h-4 w-4" />
                <Trans i18nKey="common:upload" defaults="Upload" />
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
